import React, { useEffect, useState } from 'react';
import { Button, MenuItem, Popover, Stack, TextField, Typography } from '@mui/material';

import { PlaceAutocomplete } from './PlaceAutocomplete';

export const RADIUS_OPTIONS_KM = [10, 25, 50, 100, 200];
export const DEFAULT_RADIUS_KM = 25;

const defaultTexts = {
  radius: 'Within',
  apply: 'Apply',
  clear: 'Clear',
  cancel: 'Cancel',
};

export const formatPlaceRadius = (place, radiusKm) => (place ? `${place.name}, ${radiusKm ?? DEFAULT_RADIUS_KM} km` : '');

export const PlaceRadiusPopover = ({
  anchorEl,
  onClose,
  place,
  radiusKm,
  onApply,
  isPublic = false,
  lang = 'en',
  texts: customTexts = {},
  placeTexts = {},
}) => {
  const texts = { ...defaultTexts, ...customTexts };
  const open = Boolean(anchorEl);
  const [draftPlace, setDraftPlace] = useState(place ?? null);
  const [draftRadius, setDraftRadius] = useState(radiusKm ?? DEFAULT_RADIUS_KM);

  useEffect(() => {
    if (open) {
      setDraftPlace(place ?? null);
      setDraftRadius(radiusKm ?? DEFAULT_RADIUS_KM);
    }
  }, [open, place, radiusKm]);

  const handleApply = () => {
    onApply({ place: draftPlace, radiusKm: draftRadius });
    onClose();
  };

  const handleClear = () => {
    onApply({ place: null, radiusKm: DEFAULT_RADIUS_KM });
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { mt: 1, width: 340, maxWidth: 'calc(100vw - 32px)' } } }}
    >
      <Stack gap={2} p={2}>
        <PlaceAutocomplete
          value={draftPlace}
          onChange={setDraftPlace}
          isPublic={isPublic}
          lang={lang}
          size="small"
          texts={placeTexts}
        />
        <Stack gap={0.25} mt={-0.5}>
          <Typography id="place-radius-label" variant="caption" fontWeight={600} color="text.secondary">
            {texts.radius}
          </Typography>
          <TextField
            select
            size="small"
            value={draftRadius}
            onChange={(event) => setDraftRadius(Number(event.target.value))}
            SelectProps={{ labelId: 'place-radius-label' }}
          >
            {RADIUS_OPTIONS_KM.map((radius) => (
              <MenuItem key={radius} value={radius}>{`${radius} km`}</MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button variant="text" onClick={handleClear}>{texts.clear}</Button>
          <Button variant="text" onClick={onClose}>{texts.cancel}</Button>
          <Button variant="contained" onClick={handleApply} disabled={!draftPlace}>{texts.apply}</Button>
        </Stack>
      </Stack>
    </Popover>
  );
};
